export type PlainTextBlock =
  | { type: "text"; content: string }
  | { type: "customer_link"; label: string }
  | { type: "table"; title?: string; columns: string[]; rows: string[][] }
  | { type: "list"; title?: string; items: string[] }
  | { type: "warning"; content: string }
  | { type: "chart"; title?: string }
  | { type: "done" };

export const blocksToPlainText = (blocks: PlainTextBlock[]): string =>
  blocks
    .map((block) => {
      switch (block.type) {
        case "text":
        case "warning":
          return block.content;
        case "customer_link":
          return block.label;
        case "chart":
          return block.title ?? "";
        case "table":
          return [
            block.title,
            block.columns.join("\t"),
            ...block.rows.map((row) => row.join("\t")),
          ]
            .filter((line) => line !== undefined && line !== "")
            .join("\n");
        case "list":
          return [block.title, ...block.items.map((item) => `- ${item}`)]
            .filter((line) => line !== undefined && line !== "")
            .join("\n");
        default:
          return "";
      }
    })
    .filter((part) => part !== "")
    .join("\n\n");

export const formatMessageTime = (
  createdAt: string | undefined,
  now: Date = new Date(),
): { label: string; title: string } | null => {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;

  const sameDay = date.toDateString() === now.toDateString();
  return {
    label: sameDay
      ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : date.toLocaleString([], { dateStyle: "short", timeStyle: "short" }),
    title: date.toLocaleString(),
  };
};

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isGuid = (value: string): boolean => GUID_PATTERN.test(value);

export const customerUrl = (customerId: string): string | null =>
  isGuid(customerId) ? `/Details.aspx?customerid=${customerId}` : null;

export const sanitizeHref = (href: string | undefined | null): string | null => {
  if (!href) return null;
  const value = href.trim();

  if (value.toLowerCase().startsWith("customer:")) {
    return customerUrl(value.slice("customer:".length));
  }
  if (value.startsWith("#")) return value;
  if (value.startsWith("/")) {
    return value.startsWith("//") || value.startsWith("/\\") ? null : value;
  }

  try {
    const url = new URL(value);
    return ["http:", "https:", "mailto:"].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};

export type ChartType = "bar" | "line" | "pie";

export type ChartSeries = { name: string; data: { label: string; value: number }[] };

export type TableData = { columns: string[]; rows: string[][] };

const tsvCell = (cell: string): string => cell.replace(/[\t\r\n]+/g, " ");

export const tableToTsv = ({ columns, rows }: TableData): string =>
  [columns, ...rows].map((row) => row.map(tsvCell).join("\t")).join("\n");

const csvCell = (cell: string): string =>
  /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

export const tableToCsv = ({ columns, rows }: TableData): string =>
  [columns, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");

export const csvFileName = (title?: string): string => {
  const slug = (title ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug ? `${slug}.csv` : "vecta-table.csv";
};

export const chartToTable = (series: ChartSeries[]): TableData => {
  const labels: string[] = [];
  const values = new Map<string, Map<string, number>>();
  for (const s of series) {
    const byLabel = new Map<string, number>();
    for (const point of s.data) {
      if (!values.has(point.label) && !labels.includes(point.label)) labels.push(point.label);
      byLabel.set(point.label, point.value);
    }
    for (const [label, value] of byLabel) {
      const row = values.get(label) ?? new Map<string, number>();
      row.set(s.name, value);
      values.set(label, row);
    }
  }
  return {
    columns: ["Label", ...series.map((s) => s.name)],
    rows: labels.map((label) => [
      label,
      ...series.map((s) => {
        const value = values.get(label)?.get(s.name);
        return value === undefined ? "" : String(value);
      }),
    ]),
  };
};

export const escapeControlCharsInStrings = (input: string): string => {
  let result = "";
  let inString = false;
  let escaping = false;

  for (const ch of input) {
    if (escaping) {
      escaping = false;
      result += ch;
      continue;
    }
    if (inString && ch === "\\") {
      escaping = true;
      result += ch;
      continue;
    }
    if (ch === "\"") {
      inString = !inString;
      result += ch;
      continue;
    }
    if (inString && ch === "\n") {
      result += "\\n";
    } else if (inString && ch === "\r") {
      result += "\\r";
    } else if (inString && ch === "\t") {
      result += "\\t";
    } else {
      result += ch;
    }
  }

  return result;
};

const CLOSER_FOR: Record<string, string> = { "{": "}", "[": "]" };

export const repairJsonBrackets = (input: string): string => {
  const expectedClosers: string[] = [];
  let result = "";
  let inString = false;
  let escaping = false;

  for (const ch of input) {
    if (escaping) {
      escaping = false;
      result += ch;
      continue;
    }
    if (inString) {
      if (ch === "\\") escaping = true;
      else if (ch === "\"") inString = false;
      result += ch;
      continue;
    }
    if (ch === "\"") {
      inString = true;
    } else if (ch in CLOSER_FOR) {
      expectedClosers.push(CLOSER_FOR[ch]);
    } else if (ch === "}" || ch === "]") {
      const missing: string[] = [];
      while (expectedClosers.length > 0 && expectedClosers[expectedClosers.length - 1] !== ch) {
        missing.push(expectedClosers.pop() as string);
      }
      if (expectedClosers.length === 0) {
        return input;
      }
      expectedClosers.pop();
      result += missing.join("");
    }
    result += ch;
  }

  return result + expectedClosers.reverse().join("");
};

export const parseJsonLenient = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    const escaped = escapeControlCharsInStrings(raw);
    try {
      return JSON.parse(escaped);
    } catch {
      return JSON.parse(repairJsonBrackets(escaped));
    }
  }
};

const ID_COLUMNS = ["companyid", "customerid", "id"];
const LINK_COLUMNS = ["companylink"];
const ACCOUNT_COLUMNS = ["accountno", "account no", "account"];
const NAME_COLUMNS = ["companyname", "customername", "name", "company", "customer"];

const escapeLinkText = (text: string): string => text.replace(/([\[\]\\])/g, "\\$1");

export const formatCompanyLabel = (name: string, accountNo?: string): string => {
  const account = accountNo?.replace(/\s+/g, " ").trim();
  return account ? `${name.trim()} (${account})` : name.trim();
};

export const linkCompanyRows = (
  columns: string[],
  rows: string[][],
): { columns: string[]; rows: string[][] } => {
  const lower = columns.map((column) => column.trim().toLowerCase());
  const linkIndex = lower.findIndex((column) => LINK_COLUMNS.includes(column));
  if (linkIndex >= 0) {
    const dropped = new Set(
      lower
        .map((column, index) =>
          ID_COLUMNS.includes(column) || ACCOUNT_COLUMNS.includes(column) || NAME_COLUMNS.includes(column)
            ? index
            : -1,
        )
        .filter((index) => index >= 0 && index !== linkIndex),
    );
    return {
      columns: columns.filter((_, index) => !dropped.has(index)),
      rows: rows.map((row) => row.filter((_, index) => !dropped.has(index))),
    };
  }

  const idIndex = lower.findIndex((column) => ID_COLUMNS.includes(column));
  const nameIndex = lower.findIndex((column) => NAME_COLUMNS.includes(column));

  if (idIndex < 0 || nameIndex < 0 || idIndex === nameIndex) {
    return { columns, rows };
  }
  if (!rows.every((row) => isGuid(row[idIndex]?.trim() ?? ""))) {
    return { columns, rows };
  }

  const accountIndex = lower.findIndex((column) => ACCOUNT_COLUMNS.includes(column));
  const dropped = new Set([idIndex, ...(accountIndex >= 0 ? [accountIndex] : [])]);

  const linkedRows = rows.map((row) => {
    const next = [...row];
    const name = row[nameIndex];
    if (name && !name.includes("](")) {
      const label = formatCompanyLabel(name, accountIndex >= 0 ? row[accountIndex] : undefined);
      next[nameIndex] = `[${escapeLinkText(label)}](customer:${row[idIndex].trim()})`;
    }
    return next.filter((_, index) => !dropped.has(index));
  });

  return {
    columns: columns.filter((_, index) => !dropped.has(index)),
    rows: linkedRows,
  };
};
